import React, { useEffect, useState, useCallback } from 'react';
import { Card, Button, Table, Tag, Space, Form, Row, Col, Input, DatePicker, InputNumber, Select, message, Modal } from 'antd';
import { PlusOutlined, FileTextOutlined, DeleteOutlined, EditOutlined, PrinterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import useStore from '../store/useStore';
import { ewayBillsAPI } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorDisplay from '../components/common/ErrorDisplay';
import RightDrawer from '../components/common/RightDrawer';
import DetailView, { DetailSection, DetailField } from '../components/common/DetailView';

// no TextArea used here

const EWayBills = () => {
  const { ewayBills, setEwayBills } = useStore();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [form] = Form.useForm();
  const [currentView, setCurrentView] = useState('list'); // 'list' | 'detail'
  const [selectedBill, setSelectedBill] = useState(null);
  const [editingBill, setEditingBill] = useState(null);

  const fetchBills = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await ewayBillsAPI.getAll();
      setEwayBills(res.data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [setEwayBills]);

  useEffect(() => {
    fetchBills();
  }, [fetchBills]);

  const handleOpenDrawer = () => {
    setEditingBill(null);
    form.resetFields();
    form.setFieldsValue({
      docType: 'Invoice',
      docDate: dayjs(),
      items: [
        { description: '', hsn: '', quantity: 1, uqc: 'NOS', taxableValue: 0 },
      ],
    });
    setDrawerVisible(true);
  };

  const handleEdit = (bill) => {
    setEditingBill(bill);
    setDrawerVisible(true);
    form.setFieldsValue({
      ...bill,
      docDate: bill.docDate ? dayjs(bill.docDate) : dayjs(),
    });
  };

  const handleCloseDrawer = () => {
    setDrawerVisible(false);
  };

  const handleCreate = async () => {
    try {
      setDrawerLoading(true);
      const values = await form.validateFields();
      const items = (values.items || []).map((it) => ({
        description: it.description,
        hsn: it.hsn,
        quantity: Number(it.quantity || 0),
        uqc: it.uqc,
        taxableValue: Number(it.taxableValue || 0),
      }));
      const totalValue = items.reduce((sum, it) => sum + (Number(it.taxableValue) || 0), 0);

      const payload = {
        ...values,
        docDate: values.docDate ? values.docDate.format('YYYY-MM-DD') : undefined,
        items,
        totalValue,
      };

      if (editingBill) {
        await ewayBillsAPI.update(editingBill.id, payload);
        message.success('E-Way Bill updated');
      } else {
        await ewayBillsAPI.create(payload);
        message.success('E-Way Bill created');
      }
      await fetchBills();
      handleCloseDrawer();
    } catch (err) {
      if (err?.errorFields) return; // validation error
      message.error('Failed to create E-Way Bill');
    } finally {
      setDrawerLoading(false);
    }
  };

  const handleDelete = async (record) => {
    Modal.confirm({
      title: 'Delete E-Way Bill',
      content: `Are you sure you want to delete ${record.billNo || 'this bill'}?`,
      onOk: async () => {
        try {
          await ewayBillsAPI.delete(record.id);
          await fetchBills();
          message.success('E-Way Bill deleted');
        } catch (e) {
          message.error('Failed to delete E-Way Bill');
        }
      },
    });
  };

  const columns = [
    {
      title: 'Bill No',
      dataIndex: 'billNo',
      key: 'billNo',
    },
    {
      title: 'Document',
      key: 'doc',
      render: (_, r) => (
        <span>{r.docType} • {r.docNo} • {r.docDate}</span>
      ),
    },
    {
      title: 'From → To',
      key: 'party',
      render: (_, r) => (
        <span>{r.fromTradeName || r.fromGstin} → {r.toTradeName || r.toGstin}</span>
      ),
    },
    {
      title: 'Distance (KM)',
      dataIndex: 'distanceKM',
      key: 'distanceKM',
      align: 'right',
    },
    {
      title: 'Total Value',
      dataIndex: 'totalValue',
      key: 'totalValue',
      align: 'right',
      render: (v) => (v != null ? `₹${Number(v).toFixed(2)}` : '—'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (s) => <Tag color={s === 'generated' ? 'blue' : 'default'}>{s || '—'}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, r) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => handleEdit(r)} />
          <Button danger icon={<DeleteOutlined />} onClick={() => handleDelete(r)} />
        </Space>
      ),
    },
  ];

  const handleRowClick = (record) => {
    setSelectedBill(record);
    setCurrentView('detail');
  };

  const handleBackToList = () => {
    setSelectedBill(null);
    setCurrentView('list');
  };

  const renderList = () => (
    <>
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-slate-100">E-Way Bills</h1>
          <p className="text-gray-600">Create and manage e-way bills</p>
        </div>
        <Button type="primary" icon={<PlusOutlined />} size="large" onClick={handleOpenDrawer}>
          Create E-Way Bill
        </Button>
      </div>

      <Card>
        <Table
          rowKey="id"
          dataSource={ewayBills}
          columns={columns}
          pagination={{ showSizeChanger: true }}
          onRow={(record) => ({ onClick: () => handleRowClick(record), className: 'cursor-pointer hover:bg-gray-50' })}
        />
      </Card>
    </>
  );

  const renderDetail = () => {
    if (!selectedBill) return null;
    return (
      <DetailView
        title={selectedBill.billNo || 'E-Way Bill'}
        subtitle={`${selectedBill.docType || 'Doc'} • ${selectedBill.docNo || ''} • ${selectedBill.docDate || ''}`}
        onBack={handleBackToList}
        onEdit={() => handleEdit(selectedBill)}
        onDelete={() => handleDelete(selectedBill)}
        actions={[
          {
            label: 'Print',
            icon: <PrinterOutlined />,
            onClick: () => window.print(),
          },
        ]}
        tags={[{ label: selectedBill.status || '—', color: 'blue' }]}
      >
        <Row gutter={24}>
          <Col span={16}>
            <DetailSection title="Document">
              <Row gutter={16}>
                <Col span={8}><DetailField label="Type" value={selectedBill.docType} /></Col>
                <Col span={8}><DetailField label="Number" value={selectedBill.docNo} /></Col>
                <Col span={8}><DetailField label="Date" value={selectedBill.docDate} /></Col>
              </Row>
            </DetailSection>

            <DetailSection title="Parties">
              <Row gutter={16}>
                <Col span={12}>
                  <DetailField label="From GSTIN" value={selectedBill.fromGstin} />
                  <DetailField label="From Trade Name" value={selectedBill.fromTradeName || '—'} />
                  <DetailField label="From Pincode" value={selectedBill.fromPincode} />
                </Col>
                <Col span={12}>
                  <DetailField label="To GSTIN" value={selectedBill.toGstin} />
                  <DetailField label="To Trade Name" value={selectedBill.toTradeName || '—'} />
                  <DetailField label="To Pincode" value={selectedBill.toPincode} />
                </Col>
              </Row>
            </DetailSection>

            <DetailSection title="Items">
              <Table
                size="small"
                rowKey={(r, idx) => idx}
                dataSource={selectedBill.items || []}
                pagination={false}
                columns={[
                  { title: 'Description', dataIndex: 'description', key: 'description' },
                  { title: 'HSN', dataIndex: 'hsn', key: 'hsn', width: 100 },
                  { title: 'Qty', dataIndex: 'quantity', key: 'quantity', width: 80 },
                  { title: 'UQC', dataIndex: 'uqc', key: 'uqc', width: 100 },
                  { title: 'Taxable Value', dataIndex: 'taxableValue', key: 'taxableValue', width: 140, render: (v) => `₹${Number(v || 0).toFixed(2)}` },
                ]}
              />
            </DetailSection>
          </Col>
          <Col span={8}>
            <DetailSection title="Transport">
              <DetailField label="Distance (KM)" value={selectedBill.distanceKM} />
              <DetailField label="Transporter ID" value={selectedBill.transporterId || '—'} />
              <DetailField label="Vehicle No" value={selectedBill.vehicleNo || '—'} />
            </DetailSection>
            <DetailSection title="Summary">
              <DetailField label="Total Value" value={`₹${Number(selectedBill.totalValue || 0).toFixed(2)}`} />
              <DetailField label="Status" value={selectedBill.status || '—'} />
            </DetailSection>
          </Col>
        </Row>
      </DetailView>
    );
  };

  if (loading) return <LoadingSpinner size="large" tip="Loading E-Way Bills..." />;
  if (error) return <ErrorDisplay error={error} onRetry={fetchBills} title="Failed to load E-Way Bills" />;

  return (
    <div className="space-y-6">
      {currentView === 'list' && renderList()}
      {currentView === 'detail' && renderDetail()}
      <RightDrawer
        title="Create E-Way Bill"
        open={drawerVisible}
        onClose={handleCloseDrawer}
        loading={drawerLoading}
        width={720}
      >
        <Form layout="vertical" form={form} onFinish={handleCreate} id="drawer-form">
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="docType" label="Document Type" rules={[{ required: true }]}>
                <Select
                  options={[
                    { value: 'Invoice', label: 'Invoice' },
                    { value: 'Delivery Challan', label: 'Delivery Challan' },
                    { value: 'Bill of Supply', label: 'Bill of Supply' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="docNo" label="Document No" rules={[{ required: true }]}>
                <Input placeholder="INV-001" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="docDate" label="Document Date" rules={[{ required: true }]}>
                <DatePicker className="w-full" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="fromGstin" label="From GSTIN" rules={[{ required: true }]}>
                <Input placeholder="22AAAAA0000A1Z5" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="fromTradeName" label="From Trade Name">
                <Input placeholder="Sender Pvt Ltd" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="toGstin" label="To GSTIN" rules={[{ required: true }]}>
                <Input placeholder="27BBBBB1111B2Z6" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="toTradeName" label="To Trade Name">
                <Input placeholder="Receiver LLC" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="fromPincode" label="From Pincode" rules={[{ required: true }]}>
                <Input placeholder="560001" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="toPincode" label="To Pincode" rules={[{ required: true }]}>
                <Input placeholder="400001" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="distanceKM" label="Distance (KM)" rules={[{ required: true }]}> 
                <InputNumber min={0} className="w-full" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="transporterId" label="Transporter ID">
                <Input placeholder="TRANS123" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="vehicleNo" label="Vehicle No">
                <Input placeholder="KA01AB1234" />
              </Form.Item>
            </Col>
          </Row>

          <Card title="Items" className="mb-4">
            <Form.List name="items">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field) => (
                    <Row gutter={8} key={field.key} align="middle" className="mb-2">
                      <Col span={6}>
                        <Form.Item {...field} name={[field.name, 'description']} fieldKey={[field.fieldKey, 'description']} rules={[{ required: true, message: 'Description required' }]}>
                          <Input placeholder="Description" />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item {...field} name={[field.name, 'hsn']} fieldKey={[field.fieldKey, 'hsn']}>
                          <Input placeholder="HSN" />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item {...field} name={[field.name, 'quantity']} fieldKey={[field.fieldKey, 'quantity']} rules={[{ required: true }]}>
                          <InputNumber min={1} className="w-full" placeholder="Qty" />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item {...field} name={[field.name, 'uqc']} fieldKey={[field.fieldKey, 'uqc']} rules={[{ required: true }]}>
                          <Select options={[{ value: 'NOS', label: 'NOS' }, { value: 'PCS', label: 'PCS' }]} />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item {...field} name={[field.name, 'taxableValue']} fieldKey={[field.fieldKey, 'taxableValue']} rules={[{ required: true }]}> 
                          <InputNumber min={0} step={0.01} className="w-full" placeholder="Taxable" />
                        </Form.Item>
                      </Col>
                      <Col span={2}>
                        <Button
                          danger
                          type="text"
                          shape="circle"
                          icon={<DeleteOutlined />}
                          onClick={() => remove(field.name)}
                          aria-label="Remove item"
                          size="small"
                          style={{ marginTop: -4 }}
                        />
                      </Col>
                    </Row>
                  ))}
                  <Button type="dashed" onClick={() => add({ quantity: 1, uqc: 'NOS', taxableValue: 0 })} block>
                    Add Item
                  </Button>
                </>
              )}
            </Form.List>
          </Card>
        </Form>
      </RightDrawer>
    </div>
  );
};

export default EWayBills;
